import { BinaryWriter, ProtocolError } from "../binary.js";
import { ClientOp, ServerOp } from "../opcodes.js";
/**
 * As automações que não são o auto-loot: a comida e a caçada (PRD §6).
 *
 * ## Por que elas existem, e por que juntas
 *
 * O auto-loot resolveu um problema de **atenção**: duzentas mortes por hora de caçada eram
 * seiscentos cliques de saque, e o que o jogador fazia na prática era parar de saquear. As
 * duas automações deste arquivo respondem à mesma pergunta em outras duas escalas.
 *
 * A **comida** é a menor delas e a mais indefensável de deixar manual. A saciedade dura vinte
 * minutos (`FOOD_MAX_MS`) e o que ela compra é regeneração: sem comer, a vida para de voltar.
 * O gesto é sempre o mesmo — abrir a mochila, achar o pão, dois cliques — e ele acontece três
 * vezes por hora, sempre no meio de outra coisa. Não há decisão nenhuma ali: ninguém escolhe
 * ficar com fome. Uma mecânica cuja única forma de errar é esquecer é uma mecânica que cobra
 * memória, e memória não é o recurso que este jogo quer disputar.
 *
 * A **cura** está no meio das duas em tamanho e é a mais fácil de justificar em campo: a barra
 * de vida é conferida com o olho, no meio de uma briga, enquanto a mão está ocupada andando e
 * batendo — e o gesto que ela pede é sempre o mesmo, poção ou a mesma magia de cura. O que ela
 * não faz é escolher: ela não decide fugir, não decide voltar para a cidade e não decide parar
 * de caçar. Ver `AUTOMATION_HEAL`.
 *
 * A **caçada** é a maior: uma rota gravada que o personagem percorre sozinho, matando o que o
 * ataca no caminho. Ela não é comodidade, é uma mudança no que o jogo é — e é por isso que ela
 * tem interruptor próprio, mora no personagem e é dita em voz alta na tela.
 *
 * As três viajam no mesmo pacote porque são **um** estado: a tela é uma só, o jogador liga e
 * desliga todas no mesmo lugar, e um pacote por automação abriria a janela em que o servidor
 * tem a rota nova com o piso de fome velho.
 *
 * ## Por que os dois sentidos têm formatos diferentes
 *
 * O auto-loot manda o **mesmo corpo** nos dois sentidos, e o comentário de `loot-filter.ts`
 * explica por quê: é o mesmo estado, e a única diferença é quem está contando a quem. Aqui
 * não é assim, e forçar a simetria seria copiar a forma sem a razão.
 *
 * Quem **grava** a rota é o servidor, e não a tela. Não é preferência de arquitetura: o
 * cliente não tem como gravar o degrau da escada. Ele nunca vê o personagem parado em cima
 * dela — pisar numa escada é ser movido no mesmo tick (ver `World.onPlayerArrived`) —, então
 * uma rota gravada pelo cliente registraria o tile de antes e o de depois e **nunca o do
 * meio**, que é justamente o único que precisa ser pisado para trocar de andar.
 *
 * Com o servidor gravando, o cliente deixa de ter o que mandar sobre a rota: ele manda as
 * **escolhas** (ligado, piso de fome, piso de vida) e no máximo um comando de edição
 * — "esqueça a rota", "tire o último ponto". O servidor manda o **estado**, rota inclusa.
 * Um pacote simétrico obrigaria o cliente a devolver uma rota que ele não escreveu, e a
 * primeira corrida entre um ponto recém-gravado e um clique numa caixa de seleção apagaria
 * o ponto.
 */
/** Auto-comida ligada: come sozinho quando a saciedade chega ao piso. */
export const AUTOMATION_FOOD = 1 << 0;
/** Auto-caçada ligada: o personagem percorre a rota gravada. */
export const AUTOMATION_HUNT = 1 << 1;
/** Gravando: cada passo do jogador vira ponto da rota. Ver `MAX_WAYPOINTS`. */
export const AUTOMATION_RECORDING = 1 << 2;
/**
 * Revidar sozinho: quem bater em você vira alvo, se você não tiver alvo.
 *
 * Bit próprio, e não uma consequência de `AUTOMATION_HUNT`, porque os dois se separam nas
 * duas direções e as duas são úteis. Caçar à mão com o revide ligado é a forma mais comum de
 * jogar — o clique no monstro deixa de ser obrigatório quando ele já está batendo em você. E
 * a rota andando com o revide **desligado** é a patrulha de quem quer escolher cada briga.
 *
 * O revide nunca mira em jogador, e o motivo está em `retaliate` (`systems/automation.ts`):
 * uma automação que responde a gente marca o personagem como agressor sem ninguém ter
 * clicado em nada.
 */
export const AUTOMATION_RETALIATE = 1 << 3;
/**
 * Auto-cura de vida: cura sozinho quando a vida cai abaixo do piso.
 *
 * ## Por que ela não é "a caçada bebendo poção"
 *
 * A caçada declara, em `systems/automation.ts`, que **não bebe poção e não foge**, e a frase
 * continua verdadeira: o que ela não faz é decidir *no meio da rota* que é hora de gastar um
 * recurso. Esta automação é outra coisa, e a diferença é a mesma que separa a comida da rota —
 * ela não escolhe nada sobre o mundo, só responde a um número do próprio personagem, e responde
 * igual dentro ou fora de uma caçada.
 *
 * É por isso que ela é bit próprio e não uma consequência de `AUTOMATION_HUNT`: curar à mão
 * enquanto a rota anda é possível, e caçar à mão com a cura ligada é a forma como quase todo
 * mundo vai jogar. As duas se separam nas duas direções, exatamente como o revide.
 *
 * ## Por que vida e mana são dois bits
 *
 * Porque são duas decisões econômicas diferentes. A poção de vida é o que separa apanhar de
 * morrer, e quase ninguém escolhe não bebê-la; a de mana é combustível, custa mais caro por
 * unidade, e um Knight que a bebesse sozinho estaria queimando ouro para encher uma barra que
 * ele mal usa. Um interruptor só obrigaria quem quer a primeira a aceitar a segunda.
 */
export const AUTOMATION_HEAL = 1 << 4;
/** Auto-cura de mana: bebe poção de mana quando a mana cai abaixo do piso. */
export const AUTOMATION_MANA = 1 << 5;
/**
 * Piso de vida e de mana da auto-cura, em porcentagem.
 *
 * A faixa não chega a zero, ao contrário de `MAX_HEALTH_FLOOR_PERCENT`: lá o zero significa
 * "não use esta régua", que é uma escolha com sentido — a caçada continua andando. Aqui o
 * interruptor já responde a mesma pergunta, e um piso de zero seria uma automação ligada que
 * nunca age, que é a pior das três combinações possíveis de ler numa tela.
 *
 * O teto de 90 é o mesmo argumento do teto do piso de saciedade: acima dele a automação passa
 * a agir com a barra praticamente cheia, e o que ela faria é gastar poção para repor cinco de
 * vida — o desperdício que `consume` recusa item a item, repetido a cada meio segundo.
 */
export const MIN_HEAL_FLOOR_PERCENT = 10;
export const MAX_HEAL_FLOOR_PERCENT = 90;
/**
 * Teto de pontos da rota.
 *
 * O teto existe pelo mesmo motivo do de `MAX_LOOT_FILTER`, mas com a régua **do servidor**: é
 * ele quem grava, então o número não protege contra cliente adulterado e sim contra a rota que
 * cresce para sempre enquanto alguém esquece o modo de gravação ligado.
 *
 * ## Por que deixou de ser sessenta e quatro
 *
 * A conta que justificava o sessenta e quatro media a coisa errada. Um ponto a cada
 * `WAYPOINT_SPACING` tiles dava "umas duas centenas de tiles de percurso", e duas centenas de
 * tiles é uma volta de mapa aberto — não é uma caverna. Uma caverna se percorre entrando,
 * descendo, dando a volta na sala do fundo e voltando pelo mesmo corredor, e o corredor conta
 * duas vezes. O teto velho cortava a gravação **no meio da descida**, e o sintoma era o pior
 * possível: a rota não parecia truncada, parecia uma volta que dava meia-volta cedo demais.
 *
 * Duzentos são uns seiscentos tiles, que é o tamanho de um andar inteiro de caverna com a
 * volta fechando. O custo é linear e pequeno nos três lugares onde ele aparece — mil bytes no
 * pacote de estado, duzentas linhas esparsas em `character_flags`, e um A\* por ponto
 * alcançado, que já era um por ponto.
 *
 * O teto do **formato** é 255, porque `count` é `u8` (ver `encodeAutomationState`). Subir
 * daqui até lá é trocar este número; passar de lá exige alargar o campo.
 */
export const MAX_WAYPOINTS = 200;
/**
 * Piso de saciedade que a auto-comida pode ter, em minutos.
 *
 * O teto é menor que os vinte de `FOOD_MAX_MS` de propósito: um piso de vinte minutos é
 * "coma sempre", e comer sempre é o que `feed` recusa — a refeição que não cabe é
 * **recusada**, não desperdiçada, então a automação ficaria tentando e falhando a cada
 * segundo. Quinze deixa espaço para o menor pedaço de comida do catálogo (o Cogumelo, de um
 * minuto) caber.
 */
export const MIN_FOOD_FLOOR_MINUTES = 1;
export const MAX_FOOD_FLOOR_MINUTES = 15;
/** Piso de vida em que a caçada para. `0` desliga a régua. Ver `AutomationPacket`. */
export const MAX_HEALTH_FLOOR_PERCENT = 90;
/**
 * Quantas rotas o personagem guarda com nome (PRD §6).
 *
 * ## Por que existe uma biblioteca, e não uma rota só
 *
 * Uma rota é de um **lugar**: os pontos são coordenadas, e `routeRegion` já existia para
 * impedir que a volta da caverna fosse percorrida na cidade. O que faltava era o outro lado
 * disso — quem caça em dois lugares tinha que **regravar** um deles toda vez que voltasse ao
 * outro, porque gravar por cima é o que a rota única faz. Meia hora de gravação jogada fora
 * por trocar de terreno de caça.
 *
 * Dez é o número de terrenos que uma pessoa de fato alterna, com folga. Não é um limite de
 * armazenamento — dez rotas cheias são oito mil bytes por personagem — e sim de **escolha**:
 * uma lista de cinquenta nomes deixa de ser uma lista e vira uma pasta que ninguém arruma.
 */
export const MAX_ROUTES = 10;
/**
 * Teto do nome da rota, em caracteres.
 *
 * "Caverna do Eco subsolo 1" tem vinte e quatro, e é o formato de nome que este campo existe
 * para receber: lugar mais andar. O corte é contado aqui, e não deixado para o `str8` do
 * `BinaryWriter`, porque aquele corta em **bytes** — um nome com acentos perderia letras num
 * ponto que o jogador não teria como prever ao digitá-lo.
 */
export const MAX_ROUTE_NAME = 24;
/**
 * O que o cliente pede que aconteça com a rota gravada.
 *
 * Um comando, e não a rota inteira: ver o comentário do módulo. É a única coisa que a tela
 * tem a dizer sobre uma lista que ela não escreve.
 *
 * ## Os três primeiros e os quatro últimos
 *
 * `Clear` e `DropLast` editam a rota **na mão** — a que está sendo gravada, a que a caçada
 * percorre. Os quatro de baixo falam com a biblioteca, e a diferença entre as duas metades é
 * a razão de a rota na mão continuar existindo separada: gravar é um gesto contínuo, com
 * desfazer, e salvar é o instante em que ele termina. Quem grava direto por cima de um item
 * da biblioteca perde a rota salva no primeiro passo errado, sem ter pedido nada.
 */
export const RouteCommand = {
    /** Nada — o caso de todo clique que não é sobre a rota. */
    Keep: 0,
    /** Esquece a rota que está na mão. Não toca na biblioteca. */
    Clear: 1,
    /** Tira o último ponto gravado: o desfazer de quem gravou um passo a mais. */
    DropLast: 2,
    /** Guarda a rota que está na mão em `slot`, com `name`. Sobrescreve o que estiver lá. */
    Save: 3,
    /** Traz a rota de `slot` para a mão. O que estava na mão é substituído. */
    Load: 4,
    /** Apaga a rota guardada em `slot`. A que está na mão não muda. */
    Delete: 5,
    /** Troca o nome da rota de `slot`, sem mexer nos pontos dela. */
    Rename: 6,
};
export function isRouteCommand(value) {
    return value >= RouteCommand.Keep && value <= RouteCommand.Rename;
}
/**
 * ```
 * op:u8 | flags:u8 | foodFloor:u8 | healthFloor:u8 | healFloor:u8 | manaFloor:u8
 *      | route:u8 | slot:u8 | name:str8
 * ```
 */
export function encodeAutomationRequest(packet, writer = new BinaryWriter(64)) {
    writer
        .u8(ClientOp.Automation)
        .u8(packet.flags)
        .u8(packet.foodFloorMinutes)
        .u8(packet.healthFloorPercent)
        .u8(packet.healFloorPercent)
        .u8(packet.manaFloorPercent)
        .u8(packet.route)
        .u8(packet.slot)
        .str8(packet.name.slice(0, MAX_ROUTE_NAME));
    return writer;
}
export function decodeAutomationRequest(reader) {
    const flags = reader.u8();
    const foodFloorMinutes = reader.u8();
    const healthFloorPercent = reader.u8();
    const healFloorPercent = reader.u8();
    const manaFloorPercent = reader.u8();
    const route = reader.u8();
    const slot = reader.u8();
    const name = reader.str8();
    // Comando fora do domínio vira `Keep` em vez de derrubar a conexão, pela mesma disciplina
    // que `decodeLootFilter` aplica à raridade: o campo tem sete valores num byte inteiro de
    // espaço, e um cliente de outra versão mandando o oitavo é a situação normal de um servidor
    // que já atualizou. O que **não** pode acontecer é o oitavo valor ser lido como "apague a
    // rota", e é isso que esta linha impede.
    //
    // O `slot` fora da faixa **não** é corrigido aqui, e sim recusado pelo servidor: zerá-lo
    // transformaria "apague a gaveta 200" em "apague a gaveta 0", que é um comando destrutivo
    // que ninguém deu. Ver `applyAutomation`.
    return {
        flags,
        foodFloorMinutes,
        healthFloorPercent,
        healFloorPercent,
        manaFloorPercent,
        route: isRouteCommand(route) ? route : RouteCommand.Keep,
        slot,
        name: name.slice(0, MAX_ROUTE_NAME),
    };
}
/**
 * ```
 * op:u8 | flags:u8 | foodFloor:u8 | healthFloor:u8 | healFloor:u8 | manaFloor:u8
 *      | routeRegion:u16
 *      | count:u8   (x:u16 y:u16 z:u8){count}
 *      | saved:u8   (slot:u8 name:str8 region:u16 points:u8){saved}
 * ```
 */
export function encodeAutomationState(packet, writer = new BinaryWriter(10 + MAX_WAYPOINTS * 5 + MAX_ROUTES * 32)) {
    // O corte acontece na saída, e não na entrada do outro lado, pelo mesmo argumento de
    // `encodeLootFilter`: quem escreve uma rota maior que o teto é código nosso com defeito, e
    // cortar em silêncio é preferível a derrubar a conexão de quem só andou demais gravando.
    const route = packet.route.slice(0, MAX_WAYPOINTS);
    const library = packet.library.slice(0, MAX_ROUTES);
    writer
        .u8(ServerOp.Automation)
        .u8(packet.flags)
        .u8(packet.foodFloorMinutes)
        .u8(packet.healthFloorPercent)
        .u8(packet.healFloorPercent)
        .u8(packet.manaFloorPercent)
        .u16(packet.routeRegion)
        .u8(route.length);
    for (const point of route) {
        writer.u16(point.x).u16(point.y).u8(point.z);
    }
    writer.u8(library.length);
    for (const saved of library) {
        writer
            .u8(saved.slot)
            .str8(saved.name.slice(0, MAX_ROUTE_NAME))
            .u16(saved.region)
            .u8(Math.min(saved.points, MAX_WAYPOINTS));
    }
    return writer;
}
export function decodeAutomationState(reader) {
    const flags = reader.u8();
    const foodFloorMinutes = reader.u8();
    const healthFloorPercent = reader.u8();
    const healFloorPercent = reader.u8();
    const manaFloorPercent = reader.u8();
    const routeRegion = reader.u16();
    const count = reader.u8();
    // `count` é `u8` e o teto é `MAX_WAYPOINTS`: um número entre os dois só sai de um servidor
    // com defeito ou de um frame corrompido, e nos dois casos continuar lendo produziria uma
    // rota com lixo dentro. Derruba, como todo frame malformado.
    if (count > MAX_WAYPOINTS) {
        throw new ProtocolError(`rota longa demais: ${count}`);
    }
    const route = new Array(count);
    for (let i = 0; i < count; i++) {
        route[i] = { x: reader.u16(), y: reader.u16(), z: reader.u8() };
    }
    const saved = reader.u8();
    if (saved > MAX_ROUTES) {
        throw new ProtocolError(`biblioteca grande demais: ${saved}`);
    }
    const library = new Array(saved);
    for (let i = 0; i < saved; i++) {
        library[i] = {
            slot: reader.u8(),
            name: reader.str8(),
            region: reader.u16(),
            points: reader.u8(),
        };
    }
    return {
        flags,
        foodFloorMinutes,
        healthFloorPercent,
        healFloorPercent,
        manaFloorPercent,
        routeRegion,
        route,
        library,
    };
}
//# sourceMappingURL=automation.js.map